"use client";

import { useEffect, useState } from "react";

interface LoadingSpinnerProps {
  size?: "sm" | "md" | "lg" | "xl";
  fullScreen?: boolean;
  showText?: boolean;
  text?: string;
  className?: string;
}

export default function LoadingSpinner({
  size = "md",
  fullScreen = false,
  showText = true,
  text = "Loading...",
  className = "",
}: LoadingSpinnerProps) {
  const [particles, setParticles] = useState<
    Array<{
      id: number;
      left: string;
      top: string;
      delay: string;
      duration: string;
    }>
  >([]);

  const sizeClasses = {
    sm: "w-6 h-6",
    md: "w-8 h-8",
    lg: "w-12 h-12",
    xl: "w-16 h-16",
  };

  const containerClasses = fullScreen
    ? "fixed inset-0 flex items-center justify-center z-50 bg-white"
    : size === "sm"
      ? "inline-flex items-center justify-center"
      : "flex items-center justify-center min-h-[60vh] py-8";

  useEffect(() => {
    if (fullScreen) {
      // Generate particles only for fullscreen mode
      const newParticles = Array.from({ length: 8 }, (_, i) => ({
        id: i,
        left: `${Math.random() * 100}%`,
        top: `${Math.random() * 100}%`,
        delay: `${Math.random() * 2}s`,
        duration: `${Math.random() * 2 + 1.5}s`,
      }));
      setParticles(newParticles);
    }
  }, [fullScreen]);

  return (
    <div
      className={`${containerClasses} ${className} ${fullScreen ? "mt-[60px] sm:mt-[80px]" : ""}`}
    >
      {/* Background for fullscreen */}
      {fullScreen && (
        <div className="absolute inset-0 bg-gradient-to-br from-orange-50/50 via-transparent to-purple-50/50" />
      )}

      {/* Main Content */}
      <div
        className={`relative z-10 flex flex-col items-center ${fullScreen ? "scale-90" : ""}`}
      >
        {/* Modern Logo Animation */}
        <div
          className={`relative ${sizeClasses[size]} ${showText ? "mb-3" : ""}`}
        >
          <div className="absolute inset-0 flex items-center justify-center">
            <svg
              className="w-full h-full text-orange-500"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                className="animate-draw-circle"
                cx="12"
                cy="12"
                r="8"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeDasharray="50"
                strokeDashoffset="50"
              />
              <path
                className="animate-draw-hand"
                d="M12 8v4l3 1.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray="15"
                strokeDashoffset="15"
              />
            </svg>
          </div>
          <div className="absolute inset-0 animate-pulse-ring">
            <div className="w-full h-full rounded-full border-2 border-orange-500/20" />
          </div>
        </div>

        {/* Text Content */}
        {showText && (
          <div className="text-center">
            <div className="relative">
              {fullScreen ? (
                <h1 className="text-lg font-light tracking-wide text-gray-900 animate-fade-in">
                  <span className="font-medium">Relaxing </span>
                  <span className="text-orange-500">Routine</span>
                </h1>
              ) : (
                <p className="text-sm font-medium text-gray-600 animate-fade-in">
                  {text}
                </p>
              )}
              <div className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-10 h-[1.5px] bg-gradient-to-r from-transparent via-orange-500/50 to-transparent" />
            </div>
          </div>
        )}
      </div>

      {/* Subtle Background Particles - Only for fullscreen */}
      {fullScreen && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {particles.map((particle) => (
            <div
              key={particle.id}
              className="absolute w-0.5 h-0.5 rounded-full bg-orange-500/20 animate-float-particle"
              style={{
                left: particle.left,
                top: particle.top,
                animationDelay: particle.delay,
                animationDuration: particle.duration,
              }}
            />
          ))}
        </div>
      )}

      <style jsx global>{`
        @keyframes draw-circle {
          0% {
            stroke-dashoffset: 50;
          }
          100% {
            stroke-dashoffset: 0;
          }
        }

        @keyframes draw-hand {
          0% {
            stroke-dashoffset: 15;
          }
          100% {
            stroke-dashoffset: 0;
          }
        }

        @keyframes pulse-ring {
          0% {
            transform: scale(1);
            opacity: 0.3;
          }
          50% {
            transform: scale(1.1);
            opacity: 0.5;
          }
          100% {
            transform: scale(1);
            opacity: 0.3;
          }
        }

        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(6px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes float-particle {
          0% {
            transform: translateY(0) scale(1);
            opacity: 0;
          }
          50% {
            opacity: 0.5;
          }
          100% {
            transform: translateY(-30px) scale(0.8);
            opacity: 0;
          }
        }

        .animate-draw-circle {
          animation: draw-circle 1.5s ease-out infinite;
        }

        .animate-draw-hand {
          animation: draw-hand 1s ease-out infinite;
          animation-delay: 0.3s;
        }

        .animate-pulse-ring {
          animation: pulse-ring 2s ease-in-out infinite;
        }

        .animate-fade-in {
          animation: fade-in 0.6s ease-out forwards;
        }

        .animate-float-particle {
          animation: float-particle ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
